/**
 * localStorage helpers for the in-app feedback prompt.
 *
 * Keys:
 *  - feedback:submitted     — '1' once the user has submitted feedback (stop auto-prompting)
 *  - feedback:snoozedUntil  — epoch ms until which the proactive modal stays hidden
 *  - feedback:engagement    — counter of app sessions (a proxy for "came back a few times")
 *
 * All access is wrapped in try/catch so private mode / disabled storage never throws.
 */

const SUBMITTED_KEY = 'feedback:submitted';
const SNOOZED_UNTIL_KEY = 'feedback:snoozedUntil';
const ENGAGEMENT_KEY = 'feedback:engagement';

/** Number of sessions before the proactive modal becomes eligible. */
export const ENGAGEMENT_THRESHOLD = 3;

/** Days the modal stays hidden after the user dismisses it. */
export const SNOOZE_DAYS = 30;

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function isSubmitted(): boolean {
  return safeGet(SUBMITTED_KEY) === '1';
}

export function markSubmitted(): void {
  safeSet(SUBMITTED_KEY, '1');
}

export function isSnoozed(): boolean {
  const raw = safeGet(SNOOZED_UNTIL_KEY);
  if (!raw) return false;
  const until = Number(raw);
  return Number.isFinite(until) && until > Date.now();
}

export function snooze(days = SNOOZE_DAYS): void {
  safeSet(SNOOZED_UNTIL_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
}

/** Increment the session engagement counter once and return the new value. */
export function incrementEngagement(): number {
  const current = Number(safeGet(ENGAGEMENT_KEY)) || 0;
  const next = current + 1;
  safeSet(ENGAGEMENT_KEY, String(next));
  return next;
}
